import React, { type ComponentProps } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { LabelText, useInaccessibleElement } from '@sg/garnish';
import { MapWithControls } from '@sg/radar-fe';

import { getEnvVars } from '@order/utils';

// ────────────────────────────────────────────────────────────────────────────────

export const LocationsMap = (props: LocationsMapProps) => {
  const { formatMessage } = useIntl();

  // @NOTE: The map should be not be accessible to screen readers
  // an invisible LabelText is provided instead.
  const mapAltText = formatMessage(messages.mapAltText);
  const mapA11yProps = useInaccessibleElement(true);

  return (
    <>
      <LabelText
        sizeMatch={['14']}
        style={styles.mapAltText}
        accessible={true}
        accessibilityRole="text"
        accessibilityLabel={mapAltText}
        accessibilityHint={mapAltText}
      >
        {mapAltText}
      </LabelText>

      <View style={styles.mapViewContainer} {...mapA11yProps}>
        <MapWithControls {...props} publishableKey={RADAR_SDK_KEY} />
      </View>
    </>
  );
};

// ─── Constants ────────────────────────────────────────────────────────────────

const { RADAR_SDK_KEY } = getEnvVars();

// ─── Messages ─────────────────────────────────────────────────────────────────

const messages = defineMessages({
  mapAltText: {
    defaultMessage: 'Map showing order location',
    description: 'Locations Map | Accessibility | Alt text for the map',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  mapViewContainer: {
    flex: 1,
  },

  // @NOTE: Hide map alt text label from users but leave it visible to screen readers
  mapAltText: {
    position: 'absolute',
    overflow: 'hidden',
    width: 1,
    height: 1,
    margin: -1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

export type LocationsMapProps = Omit<ComponentProps<typeof MapWithControls>, 'publishableKey'>;
